// Derivations for the StockDetail popup — peer-stock filtering, factor stats,
// z-score / percentile / rank helpers. Extracted from StockDetail.tsx to keep
// the parent component under the 500-line god-file threshold.

import { useMemo } from 'react';
import { compositeScore, prodQuality } from '@/lib/composite-score';
import { PILLAR_FACTORS } from '@/lib/pillar-factors';
import { VALUATION_FACTORS } from '@/lib/clientScore';
import { computeStats, computePercentile } from '@/lib/stock-detail-helpers';
import type { ScoredStock, StockFactors } from '@/lib/compute';

export interface StockDetailDerivations {
  peerStocks: ScoredStock[];
  peerGroupLabel: string | null;
  getZScore: (key: keyof StockFactors) => number | null;
  getPercentile: (key: keyof StockFactors, invert: boolean) => number | null;
  scoreColor: (v: number | null) => string;
  qualityRank: number | null;
  valuationRank: number | null;
  compositeRank: number | null;
  compositeForStock: number | null;
}

export function useStockDetailDerivations(stock: ScoredStock, allStocks: ScoredStock[]): StockDetailDerivations {
  const peerStocks = useMemo(() => {
    const pg = stock.peerGroup;
    if (!pg) return allStocks;
    return allStocks.filter(s => s.peerGroup === pg);
  }, [allStocks, stock]);

  const peerGroupLabel = useMemo(() => {
    const pg = stock.peerGroup;
    if (!pg || pg === 'standard') return null;
    return `vs. ${peerStocks.length} ${pg} peers`;
  }, [stock, peerStocks]);

  const factorStats = useMemo(() => {
    const stats: Record<string, { mean: number; std: number } | null> = {};
    const keySet = new Set<string>();
    for (const f of Object.values(PILLAR_FACTORS.nonFinancial).flat()) keySet.add(f.key);
    for (const f of Object.values(PILLAR_FACTORS.financial).flat()) keySet.add(f.key);
    for (const f of Object.values(PILLAR_FACTORS.reit).flat()) keySet.add(f.key);
    for (const f of VALUATION_FACTORS) keySet.add(f.key);
    const allFactorKeys = [...keySet] as (keyof StockFactors)[];
    for (const key of allFactorKeys) {
      stats[key] = computeStats(peerStocks.map(s => s.factors[key] as number | null));
    }
    return stats;
  }, [peerStocks]);

  const getZScore = (key: keyof StockFactors): number | null => {
    const val = stock.factors[key];
    const stats = factorStats[key];
    if (val === null || val === undefined || !stats) return null;
    return ((val as number) - stats.mean) / stats.std;
  };

  const getPercentile = (key: keyof StockFactors, invert: boolean): number | null => {
    return computePercentile(
      stock.factors[key] as number | null,
      peerStocks.map(s => s.factors[key] as number | null),
      invert,
    );
  };

  const scoreColor = (v: number | null): string => {
    if (v === null) return '#475569';
    if (v >= 1.5) return '#22c55e';
    if (v >= 0.5) return '#4ade80';
    if (v >= -0.5) return '#94a3b8';
    if (v >= -1.5) return '#f97316';
    return '#ef4444';
  };

  const qualityRank = useMemo(() => {
    if (stock.qualityScore === null) return null;
    const sorted = [...peerStocks]
      .filter(s => s.qualityScore !== null)
      .sort((a, b) => b.qualityScore! - a.qualityScore!);
    const idx = sorted.findIndex(s => s.symbol === stock.symbol);
    return idx >= 0 ? idx + 1 : null;
  }, [stock, peerStocks]);

  const valuationRank = useMemo(() => {
    if (stock.valuationScore === null) return null;
    const sorted = [...peerStocks]
      .filter(s => s.valuationScore !== null)
      .sort((a, b) => b.valuationScore! - a.valuationScore!);
    const idx = sorted.findIndex(s => s.symbol === stock.symbol);
    return idx >= 0 ? idx + 1 : null;
  }, [stock, peerStocks]);

  // Prefer the persisted compositeScore (computed with V_blended =
  // 0.75·peer-z V + 0.25·universe-z absolute V per the production
  // change) when present, so this matches every other place in the
  // visualizer (HoldingsResearchPanel, scoring chain, alpha basket).
  // The raw `compositeScore(Q, V_peer)` fallback only kicks in for
  // stocks where the persisted value is missing (no V_blended yet).
  const stockWithComposite = stock as typeof stock & { compositeScore?: number | null };
  const compositeForStock = stockWithComposite.compositeScore
    ?? compositeScore(prodQuality(stock), stock.valuationScore);
  const compositeRank = useMemo(() => {
    if (compositeForStock == null) return null;
    const sorted = [...peerStocks]
      .map(s => {
        const sExt = s as typeof s & { compositeScore?: number | null };
        return {
          symbol: s.symbol,
          c: sExt.compositeScore ?? compositeScore(prodQuality(s), s.valuationScore),
        };
      })
      .filter(s => s.c != null)
      .sort((a, b) => (b.c as number) - (a.c as number));
    const idx = sorted.findIndex(s => s.symbol === stock.symbol);
    return idx >= 0 ? idx + 1 : null;
  }, [compositeForStock, stock.symbol, peerStocks]);

  return {
    peerStocks, peerGroupLabel,
    getZScore, getPercentile, scoreColor,
    qualityRank, valuationRank, compositeRank, compositeForStock,
  };
}
